import { Dispatch, SetStateAction, useState } from 'react';
import { QueryObserverResult } from 'react-query';

import { ETPayButton } from '../../../../components/button';
import { Modal } from '../../../../components/modal';
import { Spinner } from '../../../../components/spinner';
import { ETPayTable } from '../../../../components/table';
import { useFloatingAlert } from '../../../../hooks/useFloatingAlert';
import { PaymentStatus } from '../../../../models/common-payments';
import { InternationalInterface } from '../../../../models/international';
import { ColumnInterface, Filters, Pagination } from '../../../../models/table';
import { fillStatesWithZeros } from '../../../../utils/states';
import {
	ExportModalContent,
	ExportModalDescription,
	ExportModalTitle,
	ItemText,
	SubText,
	Text,
} from './styles';
interface InternationalTable {
	data: InternationalInterface[];
	isLoading?: boolean;
	stateList: PaymentStatus[];
	filters: Filters;
	onFilterChange: Dispatch<SetStateAction<Filters>>;
	pagination: Pagination;
	setPagination: Dispatch<SetStateAction<Pagination>>;
	totalResults?: number;
	thisMonth?: boolean;
	today?: Date;
	pastDate?: Date;
	refetch: () => Promise<QueryObserverResult<any, unknown>>;
}

export interface ModalProps {
	loader: boolean;
	show: boolean;
	url: string;
}

const defaultModal: ModalProps = {
	loader: false,
	show: false,
	url: '',
};

const InternationalTable = ({
	data,
	isLoading,
	stateList,
	filters,
	onFilterChange,
	pagination,
	setPagination,
	today,
	pastDate,
	totalResults,
}: InternationalTable) => {
	const [modal, setModal] = useState<ModalProps>(defaultModal);
	const { handleShowAlert } = useFloatingAlert();

	const InternationalPaymentStates = ['liquidated'];

	const columns: ColumnInterface<InternationalInterface>[] = [
		{
			sortField: false,
			title: 'ID',
			propertyName: 'trackingkey',
			cellRender: ({ trackingKey }) => (
				<ItemText>
					<Text>{trackingKey}</Text>
				</ItemText>
			),
		},
		{
			sortField: false,
			title: 'Destinatario',
			propertyName: 'account',
			cellRender: ({ beneficiaryAccount, beneficiaryName }) => (
				<ItemText>
					<Text>{beneficiaryAccount}</Text>
					<SubText disabled>{beneficiaryName}</SubText>
				</ItemText>
			),
		},
		{
			sortField: false,
			title: 'País de destino',
			propertyName: 'country',
			cellRender: ({ country }) => (
				<ItemText>
					<Text>{country}</Text>
				</ItemText>
			),
		},
		{
			sortField: false,
			title: 'Tipo de cambio',
			propertyName: 'badge',
			cellRender: ({ currency, exchange }) => (
				<ItemText>
					<Text>
						{exchange} {currency}
					</Text>
				</ItemText>
			),
		},
		{
			sortField: false,
			title: 'Monto en divisa de destino',
			propertyName: 'badge',
			cellRender: ({ currency, payoutAmount }) => (
				<ItemText>
					<Text>
						{payoutAmount} {currency}
					</Text>
				</ItemText>
			),
		},
		{
			sortField: false,
			title: 'Fecha',
			propertyName: 'date',
			cellRender: ({ createdatDate, createdatTime }) => (
				<ItemText>
					<Text>{createdatDate}</Text>
					<SubText disabled>{createdatTime}</SubText>
				</ItemText>
			),
		},
	];

	const handleExportAction = async () => {
		setModal({ ...modal, loader: true, show: true });

		try {
			handleCloseModal();
		} catch (e: any) {
			handleShowAlert({
				type: 'error',
				message: 'error',
			});
			handleCloseModal();
		}
	};

	const handleCloseModal = () => {
		setModal(defaultModal);
	};

	const handleDownloadData = () => {
		if (modal.url) window.location.assign(modal.url);
		setModal(defaultModal);
	};

	return (
		<>
			{modal.show && (
				<Modal
					title="Exportar"
					show={modal.show}
					onClose={handleCloseModal}
					allowCloseModal={!modal.loader}
				>
					{modal.loader ? (
						<Spinner />
					) : (
						<ExportModalContent>
							<ExportModalTitle>Tu reporte está listo</ExportModalTitle>

							<ExportModalDescription>
								Hemos terminado de recopilar los datos de tu reporte. Haz clic en el botón para
								descargarlo.
							</ExportModalDescription>
							<ETPayButton
								title="Descargar reporte"
								variant="effect"
								onClick={handleDownloadData}
							/>
						</ExportModalContent>
					)}
				</Modal>
			)}
			<ETPayTable<InternationalInterface>
				title="Todos los pagos"
				data={data}
				isLoading={!!isLoading}
				columns={columns}
				hasFilters
				hasDateFilter
				allowExport
				stateList={fillStatesWithZeros({ states: InternationalPaymentStates, stateList })}
				filters={filters}
				onFilterChange={onFilterChange}
				hasSearch
				exportAction={handleExportAction}
				hasPagination
				allowCustomFilter
				totalResults={totalResults}
				pagination={pagination}
				setPagination={setPagination}
				today={today}
				pastDate={pastDate}
				statusContent={[['liquidated', 'Liquidados']]}
			/>
		</>
	);
};

export { InternationalTable };
